import {
  Document,
  Page,
  Text,
  View,
  Font,
  StyleSheet,
} from '@react-pdf/renderer'
import { IMerchant } from '../../../types/merchant'
import { EOrderType, IOrder, IOrderItem } from '../../../types/order'
import SarabunFont from '../../../assets/fonts/th-sarabun-new.ttf'
import SarabunFontBold from '../../../assets/fonts/th-sarabun-bold.ttf'
import dayjs from 'dayjs'
import { numberFormat } from '../../../utils'
import { VAT_TYPES } from '../../../constants/global'
import { sumBy } from 'lodash'
import { ITrackingPrintedHistory } from '../../../types/trackings-printed-history'
import { useUser } from '../../../hooks/useUser'
import { IUser } from '../../../types/user'
import { Collections } from '@mui/icons-material'
import { el } from '@fullcalendar/core/internal-common'

Font.register({ family: 'Sarabun', src: SarabunFont })
Font.register({ family: 'Sarabun', src: SarabunFontBold, fontWeight: 'bold' })

interface Props {
  orders: IOrder[]
  history: ITrackingPrintedHistory
  user?: any

  merchant?: any | IMerchant
  vatType?: VAT_TYPES
  size?: 'A4' | '80mm'
  showDate?: string
}

const sizes = {
  A4: {
    width: 8.27,
    height: 11.69,
  },
  '80mm': {
    width: 3.15,
    height: 11.69,
  },
}

const PDFTrackingSummary = (props: Props) => {
  const {
    orders,
    merchant,
    vatType = VAT_TYPES.NON_VAT,
    size = 'A4',
    showDate = 'order',
    history,
    user,
  } = props

  const fontSize = size === 'A4' ? 14 : 10
  const paperPadding = size === 'A4' ? 20 : 8
  const vat = 0.07
  const paperSize = sizes[size] || sizes.A4

  const itemValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value / 1.07
      case 'exclude':
        return value
      default:
        return value
    }
  }

  const vatValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value - value / 1.07
      case 'exclude':
        return value * vat
      default:
        return 0
    }
  }

  const subTotalValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value / 1.07
      case 'exclude':
        return value
      default:
        return value
    }
  }

  const totalValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value
      case 'exclude':
        return value * vat + value
      default:
        return value
    }
  }

  let orderTotalUnit = 0
  let totalCollectionUnit: Record<string, any> = {}
  const orderItemsAll: IOrderItem[] = orders.map((order) => order.items).flat()
  const productsReduce = orderItemsAll.reduce(
    (acc, item) => {
      if (!acc[item.productCode]) {
        acc[item.productCode] = {
          productCode: item.productCode,
          productName: item.productName,
          total: 0,
          qty: 0,
          isCollection: item.isCollection,
          collections: [],
          collectionTotal: 0,
        }
      }
      acc[item.productCode].total += item.quantity
      if (item.isCollection) {
        console.log('collection', item.collections)
        const n = sumBy(item.collections, 'quantity')
        orderTotalUnit += n
        item?.collections?.map((c) => {
          if (!totalCollectionUnit[`${item.productCode}-${c.productCode}`]) {
            totalCollectionUnit[`${item.productCode}-${c.productCode}`] = 0
          }
          totalCollectionUnit[`${item.productCode}-${c.productCode}`] +=
            c.quantity
        })
        acc[item.productCode].collections = [...(item.collections || [])]
      } else {
        orderTotalUnit += item.quantity
      }
      return acc
    },
    {} as Record<string, any>,
  )
  //sorting total max to min
  const orderItems = Object.values(productsReduce).sort(
    (a, b) => b.total - a.total,
  )

  const orderTotal = orderItems.length
  // const orderTotalUnit = sumBy(orderItems, 'qty')

  return (
    <Document>
      <Page
        size={[paperSize.width * 72, paperSize.height * 72]}
        style={{
          flexDirection: 'row',
          fontFamily: 'Sarabun',
          padding: paperPadding,
        }}
      >
        <View
          style={{
            flex: 1,
            fontSize,
            lineHeight: 1,
          }}
        >
          <Text style={{ textAlign: 'left', fontWeight: 'bold', fontSize: 22 }}>
            ใบแพ็คสินค้า
          </Text>
          <Text style={{ textAlign: 'left', fontWeight: 'bold', fontSize: 18 }}>
            วันที่ {dayjs(history.createdAt).format('DD/MM/YYYY')}
          </Text>

          <Text style={{ textAlign: 'left', fontWeight: 'bold', fontSize: 18 }}>
            จำนวนสินค้าที่จัดเตรียม: {orderTotal} รายการ/ {orderTotalUnit} ชิ้น
          </Text>

          <View
            style={{
              flexDirection: 'column',
              fontSize: 13,
              marginTop: 10,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                fontWeight: 'bold',
                ...TableStyle.b,
              }}
            >
              <Text
                style={{
                  width: '10%',
                  ...TableStyle.lt,
                  ...TableStyle.header,
                }}
              >
                ลำดับ
              </Text>
              <Text
                style={{
                  width: '60%',
                  ...TableStyle.lt,
                  ...TableStyle.header,
                }}
              >
                รายการ
              </Text>
              <Text
                style={{
                  width: '15%',
                  ...TableStyle.lt,
                  ...TableStyle.header,
                }}
              >
                รหัสสินค้า
              </Text>
              <Text
                style={{
                  width: '15%',
                  ...TableStyle.lrt,
                  ...TableStyle.header,
                }}
              >
                จำนวน
              </Text>
            </View>
            {orderItems.map(
              (
                item: {
                  productCode: string
                  productName: string
                  total: number
                  isCollection?: boolean
                  collections?: {
                    productCode: string
                    productName: string
                    quantity: number
                  }[]
                },
                itemIndex: number,
              ) => (
                <>
                  <View key={`product-${itemIndex}`} wrap={false}>
                    <View
                      key={`item-${itemIndex}`}
                      style={{
                        flexDirection: 'row',
                        ...TableStyle.b,
                      }}
                    >
                      <Text
                        style={{
                          width: '10%',
                          ...TableStyle.l,
                          ...TableStyle.header,
                        }}
                      >
                        {itemIndex + 1}
                      </Text>
                      <View
                        style={{
                          width: '60%',
                          ...TableStyle.l,
                          ...TableStyle.header,
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Text style={{ fontWeight: 'bold' }}>
                          {item.productName}
                        </Text>
                      </View>

                      <Text
                        style={{
                          width: '15%',
                          ...TableStyle.l,
                          ...TableStyle.header,
                        }}
                      >
                        {item.productCode}
                      </Text>
                      <Text
                        style={{
                          width: '15%',
                          ...TableStyle.lr,
                          ...TableStyle.header,
                        }}
                      >
                        {item.isCollection ? '' : item.total}
                      </Text>
                    </View>
                  </View>
                  {item.isCollection &&
                    item.collections?.map((collection, collectionIndex) => (
                      <View
                        key={`product-coll-${collectionIndex}`}
                        wrap={false}
                      >
                        <View
                          key={`item-coll-${collectionIndex}`}
                          style={{
                            flexDirection: 'row',
                            ...TableStyle.b,
                          }}
                        >
                          <Text
                            style={{
                              width: '10%',
                              ...TableStyle.l,
                              ...TableStyle.header,
                            }}
                          >
                            {''}
                          </Text>
                          <View
                            style={{
                              width: '60%',
                              ...TableStyle.l,
                              ...TableStyle.header,
                              textAlign: 'left',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Text style={{ paddingLeft: '5px' }}>
                              {'- '}
                              {collection.productName}
                            </Text>
                          </View>

                          <Text
                            style={{
                              width: '15%',
                              ...TableStyle.l,
                              ...TableStyle.header,
                            }}
                          >
                            {collection.productCode}
                          </Text>
                          <Text
                            style={{
                              width: '15%',
                              ...TableStyle.lr,
                              ...TableStyle.header,
                            }}
                          >
                            {totalCollectionUnit?.[
                              `${item.productCode}-${collection.productCode}`
                            ] ?? 0}
                          </Text>
                        </View>
                      </View>
                    ))}
                </>
              ),
            )}
          </View>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              fontSize: 13,
              marginTop: 10,
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              <Text style={{ textAlign: 'left', fontWeight: 'bold' }}>
                พิมพ์เมื่อ : {dayjs().format('DD/MM/YYYY HH:mm')}
              </Text>
              <Text style={{ textAlign: 'left', fontWeight: 'bold' }}>
                โดย: {user?.name ?? ''}
              </Text>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <Text style={{ fontWeight: 'bold', marginBottom: 5 }}>
                ลงชื่อ: _______________________
              </Text>
              <Text style={{ fontWeight: 'bold' }}>
                (______________________)
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  )
}

export default PDFTrackingSummary

const borderColor = '#333'

const TableStyle = StyleSheet.create({
  header: {
    padding: 5,
    textAlign: 'center',
  },
  footer: {
    padding: 5,
    textAlign: 'right',
    backgroundColor: '#f0f0f0',
  },
  l: {
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  r: {
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lr: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  t: {
    borderTopWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  tb: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lt: {
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  rt: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lrt: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  b: {
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lb: {
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  rb: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lrb: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
})
